// Wichita State University: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - WSU's Common Data Set 2025-2026 (WSU_2025_2026_CDS_OPAweb.xlsx, the
//    newest of the three editions linked on Planning and Analysis's External
//    Reporting Data page): C1 applicants, admits and enrollees for Fall 2025
//    with the residency rows (In-State, Out-of-State, and the CDS
//    "Nonresidents" row, shown as International here; Unknown is 0), C8
//    test policy for Fall 2027 ("Required for some"), C14 (no application
//    closing date), C16 (rolling notification), C21/C22 (no
//    early decision or early action plan). Its G section marks 2026-2027
//    costs as not yet available and leaves G1/G5 blank, so the cost table
//    does not use it.
//  - WSU Undergraduate Admissions: Admission Requirements (freshman assured
//    admission, "test optional for admission") and Frequently Asked
//    Questions ("Applications for the next academic year open July 1";
//    reviews "throughout the year"; no formal commitment). No WSU page
//    publishes a fall 2027 application deadline or decision dates; the
//    rolling-admission rows here come from the FAQ and the CDS.
//  - Office of Financial Aid, Cost of Attendance Undergraduate Student: the
//    2026-2027 budget for a student living on campus, 15 hours a semester.
//    Its totals equal the sum of its items. The admissions office's Tuition
//    and Fees page splits 2026-2027 costs differently (tuition and required
//    fees $10,329 in-state, room and board from $12,100); the financial aid
//    budget is used.
//  - College Scorecard API, school id 156125 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Wichita State Common Data Set 2025-2026',
        url: 'https://www.wichita.edu/services/planning_and_analysis/ERD_Explanation.php',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Wichita State Undergraduate Admissions, Admission Requirements',
        url: 'https://www.wichita.edu/admissions/undergraduate/qa.php',
        read: '2026-10-01',
    },
    faq: {
        label: 'Wichita State Undergraduate Admissions, Frequently Asked Questions',
        url: 'https://www.wichita.edu/admissions/undergraduate/faq.php',
        read: '2026-10-01',
    },
    coa: {
        label: 'Wichita State Financial Aid, Cost of Attendance Undergraduate Student',
        url: 'https://www.wichita.edu/administration/financial_aid/coa_undergrad.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?156125-Wichita-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'wichita-state-university',
    kbId: 'wichita_state_university',
    name: 'Wichita State University',
    shortName: 'Wichita State',
    aliases: ['WSU', 'Shockers'],
    location: 'Wichita, KS',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (WSU visual standards); decoration only.
    branding: {
        primary: '#FFDB00',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/wichita_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 8736,
        admitted: 6311,
        enrolled: 1738,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4784, admitted: 3361 },
            { group: 'Out-of-state', applied: 3379, admitted: 2484 },
            { group: 'International', applied: 573, admitted: 466 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency rows',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Wichita State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Wichita State has no early decision or early action plan. It has no application closing date and sends decisions on a rolling basis.',
        source: 'cds',
        where: 'sections C14, C16, C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Admission', value: 'Rolling' },
        rows: [
            { plan: 'Rolling admission', deadline: 'No closing date', decision: null, binding: false },
        ],
        note: 'Applications for the next academic year open July 1. Wichita State reviews applications throughout the year for most incoming students and recommends applying early; some scholarships and academic programs have earlier deadlines. There is no formal commitment step: completing orientation and enrollment tells WSU you plan to attend. Wichita State does not publish decision dates.',
        source: 'faq',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Wichita State is test-optional for admission. Freshmen are assured admission with a cumulative unweighted GPA of 2.25 or higher, or an ACT composite of 21 or a combined SAT score of 1060, plus a 2.00 GPA on any college work taken in high school; applicants who meet neither are reviewed individually. Applicants from a non-accredited high school or homeschool need both the ACT 21 or SAT 1060 and a 2.25 GPA.',
        source: 'requirements',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some applicants for fall 2027, which matches the homeschool and non-accredited high school rule.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [11580, 22680] },
            { item: 'Food and housing (on campus)', amounts: [13840, 13840] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1330, 1330] },
            { item: 'Personal expenses', amounts: [2430, 2430] },
            { item: 'Transportation', amounts: [1380, 1380] },
            { item: 'Student loan fees', amounts: [60, 60] },
        ],
        totals: [30620, 41720],
        note: 'For a full-time undergraduate taking 15 hours a semester and living on campus, fall 2026 and spring 2027. Out-of-state students in the Shocker City Partnership or Shocker Legacy programs pay $11,580 in tuition and fees after discounts ($30,620 in all); with Shocker Select or the Midwest Student Exchange, $15,630 ($34,670 in all).',
        source: 'coa',
        netPrice: {
            average: 10307,
            byIncome: [
                { income: '$0 to $30,000', amount: 7530 },
                { income: '$30,001 to $48,000', amount: 6774 },
                { income: '$48,001 to $75,000', amount: 9000 },
                { income: '$75,001 to $110,000', amount: 12533 },
                { income: 'Over $110,000', amount: 17409 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Wichita State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
